import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useChatService } from '../../services';
import {buildResourcesEntityPayload} from './utils';
import {
  addNewChatToHistory,
  addMemorySavedTag,
  editLastUserMessage,
  setBotMessageError,
  setChatToLoading,
  setChatToStreaming,
  setInterruptLoading,
  setIsCreatingSession,
  setIsNewChat,
  setLastActiveChatId,
  setQueuedAtForLastMessages,
  setSessionExpiry,
  setSessionId,
  setUserAndBotMessageToChat,
  updateAnswerTag,
  updateChatTitle,
  updateFinalTag,
  updateMessageCancelledTag,
  updateMessageId,
  updateThinkingTag,
  updateTransferOrProgressTag,
  upsertInterruptOnMessage,
} from '../../redux/slices/chat';
import { useDispatch, useSelector } from "react-redux";
import { Message } from "./types";
import { SessionManager } from './connection/SessionManager';
import { SSEManager } from './connection/SSEManager';
import { ConnectionManager } from "./connection/ConnectionManager";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export const useSSEChat = () => {
  const { getToken } = useAuth();
  const dispatch = useDispatch();
  const {
    getChatHistoryList,
    getLastActiveQuestions,
    createChatSession,
    sendMessageToSession,
    cancelMessageGeneration,
    respondToInterrupt
  } = useChatService();

  const {
    chats,
    projectId,
    sessionId,
    isNewChat,
    lastActiveChatId,
    contextInfo,
    chatEntity,
    sessionExpired: hasSessionExpired,
  } = useSelector((state: any) => state.chatMessages);

  // Get resources page filter state
  const {
    capacityPlanningFilters,
    resourcePageSprints
  } = useSelector((state: any) => state.resources);
  const { teams: organizationTeams } = useSelector((state: any) => state.organization);

  const [sseConnection, setSseConnection] = useState<EventSource | null>(null);

  const sseConnectionRef = useRef<EventSource | null>(null);
  const hasErrorOccurredRef = useRef(false);

  const sessionManagerRef = useRef<SessionManager | null>(null);
  const sseManagerRef = useRef<SSEManager | null>(null);
  const connectionManagerRef = useRef<ConnectionManager | null>(null);

  useEffect(() => {
    sessionManagerRef.current ??= new SessionManager(async (payload: any) => {
      return await createChatSession(payload);
    });

    sseManagerRef.current ??= new SSEManager();

    if (!connectionManagerRef.current && sessionManagerRef.current && sseManagerRef.current) {
      connectionManagerRef.current = new ConnectionManager({
        sessionManager: sessionManagerRef.current,
        sseManager: sseManagerRef.current,
        apiBaseUrl: API_BASE_URL || '',
        tokenProvider: async () => await getToken({ template: 'main' })
      });
    }
  }, []);

  const cleanupSSEConnection = useCallback(() => {
    try {
      connectionManagerRef.current?.closeAll();
    } catch { }

    sseConnectionRef.current = null;
    setSseConnection(null);
    hasErrorOccurredRef.current = false;
  }, []);

  const handleSSEMessage = useCallback((data: any) => {
    const { type, token: messageToken, chat_id, message_id } = data;

    switch (type) {
      case 'connection_established':
        break;

      case 'message_start':
        hasErrorOccurredRef.current = false; // Reset error flag for new message
        break;

      case 'chat_title':
        if (!hasErrorOccurredRef.current) {
          dispatch(updateChatTitle({
            chat_id: chat_id,
            title: messageToken
          }));
        }
        break;

      case 'thinking':
        dispatch(updateMessageId({
          chatId: chat_id,
          messageId: message_id,
          isBot: true,
        }))
        dispatch(setChatToStreaming({
          chatId: chat_id ? String(chat_id) : "new-chat",
          streaming: true
        }));
        dispatch(updateThinkingTag({
          chatId: chat_id ? String(chat_id) : "new-chat",
          token: messageToken,
        }));
        break;

      case 'progress':
      case 'transfer':
      case 'research':
      case 'summarization':
        dispatch(updateMessageId({
          chatId: chat_id,
          messageId: message_id,
          isBot: true,
        }))
        dispatch(setChatToStreaming({
          chatId: chat_id ? String(chat_id) : "new-chat",
          streaming: true
        }));
        dispatch(updateTransferOrProgressTag({
          chatId: chat_id ? String(chat_id) : "new-chat",
          token: messageToken,
          type: type
        }))
        break;

      case 'answer':
        dispatch(updateMessageId({
          chatId: chat_id,
          messageId: message_id,
          isBot: true,
        }))
        dispatch(updateAnswerTag({
          chatId: chat_id ? String(chat_id) : "new-chat",
          token: messageToken,
        }))
        break;

      case 'final_response':
        dispatch(updateFinalTag({
          chatId: chat_id ? String(chat_id) : "new-chat",
          token: messageToken,
        }))
        dispatch(updateMessageId({
          chatId: chat_id,
          messageId: message_id,
          isBot: true,
        }))
        break;

      case 'message_complete':
        break;

      case 'cancelled':
      case 'message_cancelled':
        dispatch(updateMessageId({
          chatId: data.chat_id,
          messageId: data.message_id,
          isBot: true,
        }));
        dispatch(updateMessageCancelledTag({
          token: messageToken
        }));
        break;

      case 'timeout':
        // Handle timeout messages from the backend
        hasErrorOccurredRef.current = true; // Set error flag to prevent chat title updates
        dispatch(setBotMessageError({
          chatId: chat_id ? String(chat_id) : "new-chat",
          error: messageToken || "Your request took too long to process and has timed out."
        }));
        break;

      case 'summary':
        break;

      case 'session_expired':
      case 'invalid_session':
        break;

      case 'error':
      case 'google_error':
        // Handle error messages from the backend
        hasErrorOccurredRef.current = true; // Set error flag to prevent chat title updates
        dispatch(setBotMessageError({
          chatId: chat_id ? String(chat_id) : "new-chat",
          error: messageToken || "An error occurred while processing your message."
        }));
        break;

      case 'heartbeat':
        // Heartbeat received - connection is alive, no action needed
        // The SSEManager already tracks this for connection health
        break;

      case 'simulation_approval_ui':
        dispatch(updateMessageId({
          chatId: chat_id,
          messageId: message_id,
          isBot: true,
        }))
        // Store as message-level interrupt (not tag)
        dispatch(upsertInterruptOnMessage({
          chatId: chat_id ? String(chat_id) : "new-chat",
          messageId: message_id,
          type: 'simulation_approval_ui',
          data: {
            interrupt_id: data.interrupt_id,
            work_item_id: data.work_item_id,
            plan: data.plan,
            message: data.message,
            chat_id: data.chat_id,
            message_id: data.message_id
          }
        }));
        break;

      case 'question_ui':
        dispatch(updateMessageId({
          chatId: chat_id,
          messageId: message_id,
          isBot: true,
        }))
        dispatch(upsertInterruptOnMessage({
          chatId: chat_id ? String(chat_id) : "new-chat",
          messageId: message_id,
          type: 'question_ui',
          data: {
            interrupt_id: data.interrupt_id,
            question: data.question,
            chat_id: data.chat_id,
            message_id: data.message_id
          }
        }));
        // Also treat the question as an answer token to render in the bot message
        if (data?.question) {
          dispatch(updateAnswerTag({
            chatId: chat_id ? String(chat_id) : "new-chat",
            token: data.question
          }));
          // Ensure streaming is not shown for question prompts
          dispatch(setChatToStreaming({
            chatId: chat_id ? String(chat_id) : "new-chat",
            streaming: false
          }));
        }
        break;

      case 'SimulationDiff':
        dispatch(updateMessageId({
          chatId: chat_id,
          messageId: message_id,
          isBot: true,
        }))
        // Keep a SimulationDiff interrupt on message as display-only (no clearing on respond)
        dispatch(upsertInterruptOnMessage({
          chatId: chat_id ? String(chat_id) : "new-chat",
          messageId: message_id,
          type: 'SimulationDiff',
          data: data
        }));
        // Don't dispatch setSimulation(true) here — it would trigger the
        // simulation useEffect in RoadMapDetails for the CURRENT project even
        // if the SimulationDiff is for a different project.
        // Instead, the cadencepro:project-refresh handler in RoadMapDetails
        // will set simulation mode only when the IDs match.
        window.dispatchEvent(new CustomEvent('cadencepro:project-refresh', {
          detail: { workitem_id: data.workitem_id }
        }));
        break;

      case 'memory_saved': {
        // Attach a "saved memory" tag to the current bot message
        const targetChatId = chat_id ? String(chat_id) : "new-chat";

        if (message_id) {
          dispatch(updateMessageId({
            chatId: targetChatId,
            messageId: message_id,
            isBot: true,
          }));
        }

        dispatch(addMemorySavedTag({
          chatId: targetChatId,
          memoryText: messageToken || data?.memory_text || '',
        }));
        break;
      }

      default:
        break;
    }
  }, [cleanupSSEConnection]);

  const ensureConnected = useCallback(async () => {
    if (connectionManagerRef.current?.isStreamConnected(sessionId)) {
      return String(sessionId);
    }

    const entityPayload = buildResourcesEntityPayload(
      chatEntity?.entity,
      chatEntity?.entityId,
      contextInfo?.entity_name,
      capacityPlanningFilters,
      organizationTeams,
      resourcePageSprints
    );

    const payload = {
      text: "",
      entity_id: entityPayload.entity_id,
      entity: entityPayload.entity,
      entity_name: entityPayload.entity_name,
      chat_id: lastActiveChatId || undefined,
    };

    let sid = sessionId;
    if (!sid || hasSessionExpired) {
      dispatch(setIsCreatingSession(true));

      sid = await connectionManagerRef.current!.httpWithRecovery('create', async () => {
        return await connectionManagerRef.current!.getOrCreateSession(payload);
      }, { attempts: 2 }
      );

      dispatch(setSessionId(sid));
      dispatch(setIsCreatingSession(false));
    }

    const es = await connectionManagerRef.current!.connectSSEWithRecovery(String(sid), {
      onOpen: () => { 
        // Connection opened - reset session expiry if it was set
        if (hasSessionExpired) {
          dispatch(setSessionExpiry(false));
        }
      },
      onMessage: (d) => handleSSEMessage(d),
      onError: (err) => {
        const cur = sseConnectionRef.current;
        // Only mark as expired if connection is actually closed and reconnection failed
        // The ConnectionManager will attempt reconnection automatically
        if (cur && cur.readyState === EventSource.CLOSED) {
          // Give reconnection a chance before marking as expired
          // The ConnectionManager will handle reconnection attempts
          // Only mark as expired if we're sure the session is invalid
          const errorMessage = err?.message || '';
          if (errorMessage.includes('session') || errorMessage.includes('expired') || errorMessage.includes('404')) {
            cleanupSSEConnection();
            dispatch(setSessionExpiry(true));
            dispatch(setSessionId(null));
          }
          // Otherwise, let the ConnectionManager attempt reconnection
        }
      }
    }, {
      connectTimeoutMs: 5000,
      attempts: 3
    });

    sseConnectionRef.current = es;
    setSseConnection(es);
    return sid;
  }, [chatEntity?.entityId, chatEntity?.entity, contextInfo, lastActiveChatId, handleSSEMessage, sessionId, hasSessionExpired, capacityPlanningFilters, organizationTeams, resourcePageSprints]);

  const sendMessage = useCallback(async (messageText: string, editMessageId: number | null = null) => {
    const chatId = isNewChat ? "new-chat" : lastActiveChatId;

    if (isNewChat) {
      dispatch(setIsNewChat(false));
      dispatch(setLastActiveChatId("new-chat"));
    }

    const userMessage: Message = {
      text: messageText,
      isBot: false,
      id: `user-${Date.now()}`,
      messageId: editMessageId || null,
      isTimelineExpanded: false,
      tags: [],
      isEditing: !!editMessageId // Add this property
    };

    const botMessageId = `bot-${Date.now()}`;
    const botMessage: Message = {
      text: '',
      isBot: true,
      id: botMessageId,
      messageId: null,
      tags: [],
      isTimelineExpanded: false,
      interrupts: [],
    };

    if (!editMessageId) {
      dispatch(setUserAndBotMessageToChat({
        chatId: chatId,
        userMessage: userMessage,
        botMessage: botMessage,
      }))
    }

    dispatch(setChatToLoading({
      chatId: chatId,
      loading: true
    }))
    dispatch(setChatToStreaming({
      chatId: chatId,
      streaming: true
    }))

    try {
      let targetSessionId = await ensureConnected();
      
      const entityPayload = buildResourcesEntityPayload(
        chatEntity?.entity,
        chatEntity?.entityId,
        contextInfo?.entity_name,
        capacityPlanningFilters,
        organizationTeams,
        resourcePageSprints
      );
      
      const messagePayload = {
        text: messageText,
        entity_id: entityPayload.entity_id,
        entity: entityPayload.entity,
        entity_name: entityPayload.entity_name,
        chat_id: lastActiveChatId === "new-chat" ? undefined : lastActiveChatId,
        ...(editMessageId && { message_id: editMessageId }) // Include message_id if editing
      };

      const sendResponse = await connectionManagerRef.current!.httpWithRecovery('send', async () => {
        return await sendMessageToSession(targetSessionId as any, messagePayload);
      }, {
        attempts: 2,
        onSessionInvalid: async () => {
          try { connectionManagerRef.current?.closeAll(); } catch { }
          dispatch(setSessionId(null));
          dispatch(setSessionExpiry(false));
          targetSessionId = await ensureConnected();
        }
      }
      );

      if (sendResponse?.message_id && !editMessageId) {
        dispatch(updateMessageId({
          chatId: sendResponse.chat_id,
          messageId: sendResponse.message_id,
          isBot: false,
        }))
      }

      if (sendResponse?.queued_at) {
        const targetChatId = sendResponse?.chat_id ? String(sendResponse.chat_id) : String(chatId);
        dispatch(setQueuedAtForLastMessages({
          chatId: targetChatId,
          queued_at: sendResponse.queued_at,
        }));
        if (chatId === "new-chat") {
          dispatch(setQueuedAtForLastMessages({
            chatId: "new-chat",
            queued_at: sendResponse.queued_at,
          }));
        }
      }

      if (sendResponse?.chat_id) {
        dispatch(setLastActiveChatId(sendResponse.chat_id));

        dispatch(addNewChatToHistory({
          chat_id: sendResponse.chat_id,
          title: "New Chat",
          project_id: projectId || ""
        }));
      }

      if (!lastActiveChatId && !sendResponse?.chat_id) {
        getChatHistoryList(projectId);
        getLastActiveQuestions(projectId);
      }

    } catch (error) {
      console.error("error", error);
      dispatch(setChatToLoading({
        chatId: chatId,
        loading: false
      }))
      dispatch(setChatToStreaming({
        chatId: chatId,
        streaming: false
      }))

      dispatch(setBotMessageError({
        chatId: chatId,
        error: "Sorry, an error occurred while sending your message. Please try again."
      }))
    }
  }, [chatEntity?.entityId, chatEntity?.entity, contextInfo, lastActiveChatId, sessionId, hasSessionExpired, ensureConnected, sseConnection, projectId, capacityPlanningFilters, organizationTeams, resourcePageSprints]);

  const editMessage = useCallback(async (chatId: string | number | null, messageId: number, newText: string) => {
    if (!messageId || !newText.trim()) return;

    dispatch(editLastUserMessage({
      chatId: chatId ? String(chatId) : "new-chat",
      messageId: messageId,
      text: newText,
    }))

    await sendMessage(newText, messageId);
  }, [sendMessage]);

  const cleanupOnChatChange = useCallback(() => {
    if (sessionId) {
      cleanupSSEConnection();
      dispatch(setSessionId(null))
      dispatch(setSessionExpiry(false))
      dispatch(setIsCreatingSession(false))
    }
  }, [sessionId, cleanupSSEConnection]);

  const handleCancelMessage = useCallback(async () => {
    if (sessionId) {
      try {
        await cancelMessageGeneration(sessionId);

        dispatch(updateMessageCancelledTag({ token: 'Generation cancelled by user' }));
        dispatch(setChatToLoading({
          chatId: lastActiveChatId,
          loading: false
        }))
        dispatch(setChatToStreaming({
          chatId: lastActiveChatId,
          streaming: false
        }))
      } catch (error) {
        console.error("error", error);
      }
    }
  }, [sessionId]);

  // Helper: select latest interrupt from Redux state
  const getLatestInterruptContext = useCallback((chatId?: string | number, messageId?: number | null) => {
    const cid = chatId ? String(chatId) : String(lastActiveChatId);
    const chat = chats?.[cid];
    if (!chat || !Array.isArray(chat.messages)) return null;

    if (messageId) {
      const idx = chat.messages.findIndex((m: any) => m.messageId === messageId && m.isBot);
      if (idx !== -1) {
        const msg = chat.messages[idx];
        const intr = Array.isArray(msg.interrupts) ? [...msg.interrupts].reverse().find((i: any) => i?.type !== 'SimulationDiff') : null;
        if (intr) {
          return { chatId: cid, messageId: msg.messageId, interruptId: intr?.data?.interrupt_id };
        }
      }
    }

    for (let i = chat.messages.length - 1; i >= 0; i--) {
      const msg = chat.messages[i];
      if (!msg?.isBot) continue;
      const intr = Array.isArray(msg.interrupts) ? [...msg.interrupts].reverse().find((i: any) => i?.type !== 'SimulationDiff') : null;
      if (intr) {
        return { chatId: cid, messageId: msg.messageId, interruptId: intr?.data?.interrupt_id };
      }
    }
    return null;
  }, [chats, lastActiveChatId]);

  // Handle interrupt responses (for both active and historical interrupts)
  const handleInterruptResponse = useCallback(async (responseType: string, responseData: any = {}, interruptId?: string, chatId?: string | number, messageId?: number | null) => {
    const derived = getLatestInterruptContext(chatId, messageId || null);
    const targetInterruptId = interruptId || derived?.interruptId;
    const finalChatId = chatId || derived?.chatId || lastActiveChatId;
    const finalMessageId = messageId || derived?.messageId;

    if (!targetInterruptId) {
      console.error('No interrupt ID available to respond to');
      return;
    }

    try {
      dispatch(setInterruptLoading(true));

      const currentSessionId = await ensureConnected();

      const respondResult = await connectionManagerRef.current!.httpWithRecovery('interrupt', async () => {
        return await respondToInterrupt(
          currentSessionId,
          targetInterruptId,
          responseType,
          responseData,
          finalChatId,
          finalMessageId
        );
      }, {
        attempts: 2,
        onSessionInvalid: async () => {
          try { connectionManagerRef.current?.closeAll(); } catch { }
          dispatch(setSessionId(null));
          dispatch(setSessionExpiry(false));
          await ensureConnected();
        }
      }
      );

      try {
        const cid = String(finalChatId);
        const chat = chats?.[cid];
        if (chat && Array.isArray(chat.messages)) {
          const idx = typeof finalMessageId === 'number'
            ? chat.messages.findIndex((m: any) => m.messageId === finalMessageId && m.isBot)
            : chat.messages.findLastIndex?.((m: any) => m.isBot)
            ?? (() => {
              for (let i = chat.messages.length - 1; i >= 0; i--) { if (chat.messages[i].isBot) return i; }
              return -1;
            })();

          if (idx !== -1) {
            const msg = chat.messages[idx];
            if (Array.isArray(msg.interrupts)) {
              if (responseType === 'approve' || responseType === 'modify' || responseType === 'cancel') {
                // Mark simulation_approval_ui as read-only so UI shows approved state
                const updated = msg.interrupts.map((i: any) => i.type === 'simulation_approval_ui' ? ({ ...i, data: { ...i.data, read_only: true } }) : i);
                // Write back through upsert (replace existing)
                updated.filter((i: any) => i.type === 'simulation_approval_ui').forEach((i: any) => {
                  dispatch(upsertInterruptOnMessage({ chatId: cid, messageId: msg.messageId, type: 'simulation_approval_ui', data: i.data }));
                });
              }
            }
          }
        }
      } catch { }

      if (respondResult?.message_id) {
        dispatch(updateMessageId({
          chatId: respondResult?.chat_id || finalChatId,
          messageId: respondResult.message_id,
          isBot: false,
        }));
      }

    } catch (error) {
      try {
        const axiosErr = error as any;
        const status = axiosErr?.response?.status;
        if (status === 401 || status === 403) {
          connectionManagerRef.current?.closeAll();
          dispatch(setSessionId(null));
          dispatch(setSessionExpiry(false));
          const ensuredId = await ensureConnected();
          if (ensuredId) {
            const retry = await respondToInterrupt(
              ensuredId,
              targetInterruptId as string,
              responseType,
              responseData,
              finalChatId,
              finalMessageId
            );
            if (retry?.message_id) {
              dispatch(updateMessageId({
                chatId: retry?.chat_id || finalChatId,
                messageId: retry.message_id,
                isBot: false,
              }));
            }
            return;
          }
        }
      } catch (retryErr) {
        console.error('Failed to retry interrupt response after session recreation:', retryErr);
      }
      console.error('Failed to respond to interrupt:', error);
    } finally {
      dispatch(setInterruptLoading(false));
    }
  }, [sessionId, hasSessionExpired, lastActiveChatId, sseConnection, ensureConnected, getLatestInterruptContext, chats]);

  // New: Answer a question interrupt using the same UI flow as a normal send
  const respondWithUserMessage = useCallback(async (
    responseType: 'answer' | 'modify' | 'approve' | 'cancel',
    answerText: string,
    opts?: {
      interruptId?: string;
      chatId?: string | number;
      messageId?: number | null;
    }
  ) => {
    const chatId = isNewChat ? "new-chat" : lastActiveChatId;

    if (isNewChat) {
      dispatch(setIsNewChat(false));
      dispatch(setLastActiveChatId("new-chat"));
    }

    const userMessage: Message = {
      text: answerText,
      isBot: false,
      id: `user-${Date.now()}`,
      messageId: null,
      isTimelineExpanded: false,
      tags: [],
      isEditing: false,
    };

    const botMessageId = `bot-${Date.now()}`;
    const botMessage: Message = {
      text: '',
      isBot: true,
      id: botMessageId,
      messageId: null,
      tags: [],
      isTimelineExpanded: false,
      interrupts: [],
    };

    dispatch(setUserAndBotMessageToChat({
      chatId: chatId,
      userMessage,
      botMessage,
    }));

    dispatch(setChatToLoading({
      chatId: chatId,
      loading: true,
    }));
    dispatch(setChatToStreaming({
      chatId: chatId,
      streaming: true,
    }));

    const responsePayload = (responseType === 'answer' || responseType === 'modify')
      ? { answer: answerText }
      : {};

    await handleInterruptResponse(
      responseType,
      responsePayload,
      opts?.interruptId,
      opts?.chatId,
      opts?.messageId
    );
  }, [isNewChat, lastActiveChatId, dispatch, handleInterruptResponse]);

  // New: Answer/Modify using edited user message (no new message is created)
  const respondWithEditedMessage = useCallback(async (
    responseType: 'answer' | 'modify' | 'approve' | 'cancel',
    editText: string,
    opts?: {
      interruptId?: string;
      chatId?: string | number;
      botMessageId?: number | null; // bot message before the edited user message
      userMessageId?: number | null; // the edited user message id
    }
  ) => {
    const chatId = isNewChat ? "new-chat" : lastActiveChatId;

    if (opts?.userMessageId) {
      dispatch(editLastUserMessage({
        chatId: chatId ? String(chatId) : "new-chat",
        messageId: opts.userMessageId,
        text: editText,
      }));
    }

    dispatch(setChatToLoading({ chatId: chatId, loading: true }));
    dispatch(setChatToStreaming({ chatId: chatId, streaming: true }));

    const responsePayload = (responseType === 'answer' || responseType === 'modify')
      ? { answer: editText }
      : {};

    await handleInterruptResponse(
      responseType,
      responsePayload,
      opts?.interruptId,
      opts?.chatId,
      opts?.botMessageId ?? null
    );
  }, [isNewChat, lastActiveChatId, dispatch, handleInterruptResponse]);

  useEffect(() => {
    return () => {
      cleanupSSEConnection();
    };
  }, [cleanupSSEConnection]);

  // Connection health monitoring - check connection health periodically
  useEffect(() => {
    if (!sessionId || !sseConnection) {
      return;
    }

    const healthCheckInterval = setInterval(() => {
      if (connectionManagerRef.current && sessionId) {
        const isHealthy = connectionManagerRef.current.isStreamConnected(sessionId);
        
        if (!isHealthy && sseConnectionRef.current) {
          // Connection appears unhealthy - trigger reconnection
          const currentEs = sseConnectionRef.current;
          if (currentEs && currentEs.readyState === EventSource.CLOSED) {
            console.warn('Connection health check failed - connection is closed');
            // The ConnectionManager's error handler will trigger reconnection
            // But we can also manually trigger it here
            if (connectionManagerRef.current && sessionId) {
              connectionManagerRef.current.attemptReconnection(String(sessionId), {
                onOpen: () => {
                  dispatch(setSessionExpiry(false));
                },
                onMessage: (d) => handleSSEMessage(d),
                onError: (err) => {
                  const errorMessage = err?.message || '';
                  if (errorMessage.includes('session') || errorMessage.includes('expired') || errorMessage.includes('404')) {
                    cleanupSSEConnection();
                    dispatch(setSessionExpiry(true));
                    dispatch(setSessionId(null));
                  }
                }
              }).catch(console.error);
            }
          }
        }
      }
    }, 60000); // Check every 60 seconds

    return () => {
      clearInterval(healthCheckInterval);
    };
  }, [sessionId, sseConnection, handleSSEMessage, cleanupSSEConnection]);

  return {
    sseConnection,
    isSSEConnected: sseConnection?.readyState === EventSource.OPEN,

    sendMessage,
    handleCancelMessage,
    cleanupOnChatChange,
    cleanupSSEConnection,
    editMessage,
    handleInterruptResponse,
    respondWithUserMessage,
    respondWithEditedMessage,
  };
}; 